<template>
  <ComboboxRoot
    v-model="computedModel"
    class="relative"
    :filter-function="filterFunction"
    :display-value="displayValue"
    :disabled="disabled"
    @update:search-term="debouncedSearch"
  >
    <ComboboxAnchor
      class="px-6 pb-3.5 pt-6 focus-within:ring-2 w-full bg-white relative"
      :class="!borderless && 'border border-brand-secondary/20 shadow-lg rounded-md'"
    >
      <label :for="internalId" class="block absolute top-2 !text-base font-medium text-brand-black/50 scale-100 origin-left !leading-none">
        {{ label }}
      </label>
      <ComboboxTrigger class="w-full relative h-full">
        <ClientOnly>
          <ComboboxInput
            :id="internalId"
            autocomplete="off"
            class="block w-full border-0 p-0 placeholder:text-brand-black/20 focus:ring-0 text-sm md:text-base bg-transparent"
            :placeholder="placeholder"
          />
          <button
            v-if="!disabled"
            class="absolute -right-3 bottom-3.5"
            :class="!computedModel || isLoading ? 'hidden' : 'block'"
            type="button"
            @click="computedModel = undefined"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 32 32"
              fill="currentColor"
              aria-hidden="true"
              class="h-4 w-4 text-brand-black/20"
            ><path d="M23.057 7.057l-16 16c-0.52 0.52-0.52 1.365 0 1.885s1.365 0.52 1.885 0l16-16c0.52-0.52 0.52-1.365 0-1.885s-1.365-0.52-1.885 0z" /><path d="M7.057 8.943l16 16c0.52 0.52 1.365 0.52 1.885 0s0.52-1.365 0-1.885l-16-16c-0.52-0.52-1.365-0.52-1.885 0s-0.52 1.365 0 1.885z" /></svg>
          </button>
        </ClientOnly>
      </ComboboxTrigger>

      <div v-if="isLoading" class="absolute right-4 inline-flex items-center justify-center h-full top-0">
        <LoadingSpinner class="h-5 w-5 " />
      </div>
    </ComboboxAnchor>

    <ComboboxContent
      class="locations-content absolute z-20 w-full mt-2 min-w-[90vw] lg:min-w-[20rem] max-h-[42dvh] bg-white rounded shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)] will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
      :position="borderless ? 'popper' : 'inline'"
      :align="borderless ? 'start' : 'center'"
    >
      <ComboboxViewport class="p-1.5 min-h-8">
        <span v-if="!isLoading && !Boolean(options.length)" class="text-brand-black/40 text-xs font-medium text-center py-2 px-4">
          No hay estaciones disponibles
        </span>

        <ComboboxItem
          v-for="option in options"
          :key="option.id"
          :value="option"
          class="text-sm leading-none text-brand-black/90 rounded-[3px] flex items-center min-h-[3rem] px-6 relative select-none data-[disabled]:text-brand-black/20 data-[disabled]:pointer-events-none data-[highlighted]:outline-none hover:data-[highlighted]:outline-none data-[highlighted]:bg-brand-primary/10 data-[highlighted]:text-brand-black data-[state=checked]:text-brand-secondary cursor-pointer"
          @select="onSelect"
        >
          <ComboboxItemIndicator
            class="absolute left-0 w-6 inline-flex items-center justify-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
            ><path fill="currentColor" d="m10 17l5-5l-5-5v10z" /></svg>
          </ComboboxItemIndicator>
          <BaseLocationsAutocompleteItem :item="option" />
        </ComboboxItem>
        <BaseErrorBag v-if="apiError" class="mb-2 px-3 mt-2">
          {{ apiError }}
        </BaseErrorBag>
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxRoot>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { ComboboxAnchor, ComboboxContent, ComboboxInput, ComboboxItem, ComboboxItemIndicator, ComboboxRoot, ComboboxTrigger, ComboboxViewport } from 'radix-vue';
import BaseLocationsAutocompleteItem from './BaseLocationsAutocompleteItem.vue';
import { useApi } from '~/composables/useApi';
import { useDebounce } from '~/composables';
import type { Station } from '~/types';
import { serializeQueryParams } from '~/lib/utils';
import BaseErrorBag from './BaseErrorBag.vue';
import LoadingSpinner from '~/components/icons/LoadingSpinner.vue';

interface Props {
  modelValue?: Station
  borderless?: boolean
  disabled?: boolean
  label?: string
  id?: string
  forSubscriptions?: boolean,
  placeholder?: string
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  borderless: false,
  disabled: false,
  label: 'Lugar de recogida',
  id: 'locations-autocomplete',
  forSubscriptions: true,
  placeholder: 'Buscar estación...',
});

const emit = defineEmits(['search', 'select', 'loaded']);
const computedModel = defineModel<Props['modelValue']>();
const cachedSearchTerm = ref();
const apiError = ref();
// eslint-disable-next-line no-undef
const generatedId = useId();
const internalId = computed(() => props.id || generatedId);

const { getStation } = useApi();
const isLoading = ref(true);
const options = ref<Station[]>([]);

async function fetch (params?: string, bypassLoaded = false) {
  try {
    isLoading.value = true;
    if (props.forSubscriptions) {
      params = params ? `${params}&subscription=true` : '?subscription=true';
    }
    const { data } = await getStation(params);
    options.value = data.results;
    if (!bypassLoaded) {
      emit('loaded', data?.results || []);
    }
    computedModel.value = options.value.find(v => String(v.id) === String(props.modelValue?.id));
    apiError.value = undefined;
  } catch (error) {
    console.error(error);
    apiError.value = (error as Error)?.message;
  } finally {
    isLoading.value = false;
  }
}

fetch();

function onSelect (v: any) {
  emit('select', v);
}

async function onSearch (term: string) {
  // To prevent multiple requests with the same term
  if (!cachedSearchTerm.value || term === cachedSearchTerm.value) {
    cachedSearchTerm.value = term;
    return;
  }

  cachedSearchTerm.value = term;
  const params = term ? serializeQueryParams({ phrase: term }) : '';
  onSelect(term);
  await fetch(params, true);
}

const debouncedSearch = useDebounce(onSearch, 300);

function displayValue (v: Station) {
  return v.name as string;
}

function filterFunction (list: Station[], searchTerm: string) {
  return list.filter((station: Station) => {
    return station.name && station.name.toLowerCase().includes(searchTerm.toLowerCase());
  });
}
</script>

<style scoped>
.anchor {
  @apply min-w-[160px] inline-flex items-center justify-between rounded px-[15px] text-[13px] leading-none h-[35px] gap-[5px] bg-white text-black shadow-[0_2px_10px] shadow-black/10 hover:bg-red-200 focus:shadow-[0_0_0_2px] focus:shadow-black data-[placeholder]:text-brand-secondary outline-none;
}
</style>
